import { ChangeEvent, Dispatch, SetStateAction, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import {
  Modal,
  Button,
  CardMedia,
  IconButton,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormHelperText,
} from "@mui/material";
import { useUploadFileMutation } from "../store/api/commonAPI";
import { useCreateProductMutation, useUpdateProductMutation, useGetCategoryListQuery, ProductItem } from "../store/api/manageAPI";
import { showToast, showError } from "../constants/toasts";
import Loader from "../constants/Loader";
import { PRODUCT_TYPE, VARIANT_TYPE } from "../constants/enums";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editProduct?: ProductItem | null;
}

export default function AddProduct({ open, onClose, setOpen, editProduct }: ModalProps) {
  const [image, setImage] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");

  const [type, setType] = useState<string>("0");
  const [category, setCategory] = useState<string>("");
  const [variant, setVariant] = useState<string>("0");
  const [name, setName] = useState<string>("");
  const [ingredients, setIngredients] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [size, setSize] = useState<string>("");
  const [price, setPrice] = useState<string>("");
  const [doublePrice, setDoublePrice] = useState<string>("");

  const [errors, setErrors] = useState<{
    image?: string;
    category?: string;
    name?: string;
    ingredients?: string;
    description?: string;
    type?: string;
    variant?: string;
    size?: string;
    price?: string;
    doublePrice?: string;
  }>({});

  const isEditMode = !!editProduct;

  // Upload file and create/update product mutations
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();

  const isLoading = isUploading || isCreating || isUpdating;

  // Fetch categories for dropdown
  const { data: categoriesData } = useGetCategoryListQuery({
    page: 1,
    limit: 100, // Get all categories
  });

  const categories = categoriesData?.data?.list || [];

  // Reset form when modal opens/closes or when editProduct changes
  useEffect(() => {
    if (!open) {
      setImage("");
      setImageFile(null);
      setUploadedImageUrl("");
      setFileName("");
      setType("0");
      setCategory("");
      setVariant("0");
      setName("");
      setIngredients("");
      setDescription("");
      setSize("");
      setPrice("");
      setDoublePrice("");
      setErrors({});
    } else if (editProduct) {
      // Populate form with edit data
      setName(editProduct.name || "");
      setIngredients(editProduct.ingredients || "");
      setDescription(editProduct.description || "");
      setCategory(editProduct.categoryDetails?._id || editProduct.categoryId || "");
      
      // Set type
      if (editProduct.type === PRODUCT_TYPE.SIZE) {
        setType("size");
        setSize(editProduct.size?.toString() || "");
      } else if (editProduct.type === PRODUCT_TYPE.VARIANT) {
        setType("variant");
        if (editProduct.variant === VARIANT_TYPE.SINGLE) {
          setVariant("single");
        } else if (editProduct.variant === VARIANT_TYPE.DOUBLE) {
          setVariant("double");
          setDoublePrice(editProduct.doublePrice?.toString() || "");
        }
      }
      
      setPrice(editProduct.price?.toString() || "");
      
      // Set image
      const existingImage = editProduct.image || "";
      setImage(existingImage);
      setUploadedImageUrl(existingImage);
      setImageFile(null);
      setFileName("");
      setErrors({});
    } else {
      // Reset for add mode
      setImage("");
      setImageFile(null);
      setUploadedImageUrl("");
      setFileName("");
      setType("0");
      setCategory("");
      setVariant("0");
      setName("");
      setIngredients("");
      setDescription("");
      setSize("");
      setPrice("");
      setDoublePrice("");
      setErrors({});
    }
  }, [open, editProduct]);

  // Category Change
  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value as string);
    if (errors.category) {
      setErrors((prev) => ({ ...prev, category: undefined }));
    }
  };

  // Type Change
  const handleTypeChange = (event: SelectChangeEvent) => {
    setType(event.target.value as string);
    setVariant("0"); // reset variant when type changes
    setSize("");
    setPrice("");
    setDoublePrice("");
    if (errors.type) {
      setErrors((prev) => ({ ...prev, type: undefined }));
    }
  };

  // Variant Change
  const handleVariantChange = (event: SelectChangeEvent) => {
    setVariant(event.target.value as string);
    setPrice("");
    setDoublePrice("");
    if (errors.variant) {
      setErrors((prev) => ({ ...prev, variant: undefined }));
    }
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    if (files && files[0]) {
      const file = files[0];
      
      // Validate file type
      if (!file.type.includes("image")) {
        setErrors((prev) => ({ ...prev, image: "Please select a valid image file" }));
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, image: "Image size should be less than 5MB" }));
        return;
      }

      setImageFile(file);
      setFileName(file.name);
      setImage(URL.createObjectURL(file));
      // Clear image error when user selects a new image
      if (errors.image) {
        setErrors((prev) => ({ ...prev, image: undefined }));
      }
    }
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Validate image - required for add mode, optional for edit mode (if already has image)
    if (!isEditMode && !imageFile) {
      newErrors.image = "Please upload an image";
    } else if (isEditMode && !imageFile && !uploadedImageUrl) {
      newErrors.image = "Please upload an image";
    }

    // Validate category
    if (!category || category === "") {
      newErrors.category = "Please select a category";
    }

    // Validate name
    if (!name.trim()) {
      newErrors.name = "Product name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Product name must be at least 2 characters";
    } else if (name.trim().length > 100) {
      newErrors.name = "Product name must be less than 100 characters";
    }

    // Validate ingredients
    if (!ingredients.trim()) {
      newErrors.ingredients = "Ingredients are required";
    } else if (ingredients.trim().length < 3) {
      newErrors.ingredients = "Ingredients must be at least 3 characters";
    }

    // Validate description
    if (!description.trim()) {
      newErrors.description = "Description is required";
    } else if (description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }

    // Validate type
    if (!type || type === "0") {
      newErrors.type = "Please select a type";
    }

    // If type is SIZE
    if (type === "size") {
      if (!size.trim()) {
        newErrors.size = "Size is required";
      } else {
        const sizeNum = parseFloat(size);
        if (isNaN(sizeNum) || sizeNum <= 0) {
          newErrors.size = "Size must be a positive number";
        }
      }

      if (!price.trim()) {
        newErrors.price = "Price is required";
      } else {
        const priceNum = parseFloat(price);
        if (isNaN(priceNum) || priceNum <= 0) {
          newErrors.price = "Price must be a positive number";
        }
      }
    }

    // If type is VARIANT
    if (type === "variant") {
      if (!variant || variant === "0") {
        newErrors.variant = "Please select a variant";
      } else {
        if (!price.trim()) {
          newErrors.price = "Price is required";
        } else {
          const priceNum = parseFloat(price);
          if (isNaN(priceNum) || priceNum <= 0) {
            newErrors.price = "Price must be a positive number";
          }
        }

        // If variant is DOUBLE, validate doublePrice
        if (variant === "double") {
          if (!doublePrice.trim()) {
            newErrors.doublePrice = "Double price is required";
          } else {
            const doublePriceNum = parseFloat(doublePrice);
            if (isNaN(doublePriceNum) || doublePriceNum <= 0) {
              newErrors.doublePrice = "Double price must be a positive number";
            }
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      showError("Please fix the errors in the form");
      return;
    }

    try {
      let imageUrl = uploadedImageUrl;

      // Step 1: Upload image if a new file is selected
      if (imageFile) {
        const uploadResult = await uploadFile({ file: imageFile }).unwrap();
        
        // Extract image URL from upload response
        imageUrl = uploadResult.data?.data || uploadResult.data;
        
        if (!imageUrl) {
          showError("Failed to get image URL from server.");
          return;
        }
      }

      // If no image URL available, show error
      if (!imageUrl) {
        showError("Please upload an image");
        return;
      }

      // Step 2: Prepare payload based on type
      const typeValue = type === "size" ? PRODUCT_TYPE.SIZE : PRODUCT_TYPE.VARIANT;
      
      const payload: any = {
        image: imageUrl,
        categoryId: category,
        name: name.trim(),
        ingredients: ingredients.trim(),
        description: description.trim(),
        type: typeValue,
        price: parseFloat(price),
      };

      // Add size if type is SIZE
      if (type === "size") {
        payload.size = parseFloat(size);
      }

      // Add variant and doublePrice if type is VARIANT
      if (type === "variant") {
        const variantValue = variant === "single" ? VARIANT_TYPE.SINGLE : VARIANT_TYPE.DOUBLE;
        payload.variant = variantValue;
        
        if (variant === "double") {
          payload.doublePrice = parseFloat(doublePrice);
        }
      }

      // Log payload for debugging
      console.log("🚀 Product Payload:", payload);

      // Step 3: Create or update product
      if (isEditMode && editProduct?._id) {
        // Update existing product
        const updateResponse = await updateProduct({
          id: editProduct._id,
          ...payload,
        }).unwrap();

        // Show success message from API response or default message
        const successMessage =
          updateResponse?.message || `Product "${name.trim()}" updated successfully`;
        showToast(successMessage);
      } else {
        // Create new product
        const createResponse = await createProduct(payload).unwrap();

        // Show success message from API response or default message
        const successMessage =
          createResponse?.message || `Product "${name.trim()}" created successfully`;
        showToast(successMessage);
      }

      // Reset form and close modal
      setImage("");
      setImageFile(null);
      setUploadedImageUrl("");
      setFileName("");
      setType("0");
      setCategory("");
      setVariant("0");
      setName("");
      setIngredients("");
      setDescription("");
      setSize("");
      setPrice("");
      setDoublePrice("");
      setErrors({});
      setOpen(false);
    } catch (error: any) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} product:`, error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        `Failed to ${isEditMode ? "update" : "create"} product. Please try again.`;
      showError(errorMessage);
    }
  };

  return (
    <>
      <Loader isLoad={isLoading} />
      <Modal className="modal addType_modal" open={open} onClose={onClose}>
        <div className="modal-dialog">
          <div className="modal-body">
            <div className="modal_title">
              <h2>{isEditMode ? "Edit Product" : "Add Product"}</h2>
              <button
                type="button"
                className="btn-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
                disabled={isLoading}
              >
                <CloseIcon />
              </button>
            </div>

            <form className="form" onSubmit={handleSubmit}>
              <div className="gap_p">
                {/* Upload Image */}
                <div className="control_group">
                  <label>
                    Upload Image<span className="req">*</span>
                  </label>

                  {image ? (
                    <div className="upload_image">
                      <div className="upload_image_holder">
                        <figure>
                          <CardMedia component="img" image={image} alt="Product" />
                        </figure>

                        <IconButton
                          onClick={() => {
                            setImage("");
                            setImageFile(null);
                            setUploadedImageUrl("");
                            setFileName("");
                            if (errors.image) {
                              setErrors((prev) => ({ ...prev, image: undefined }));
                            }
                          }}
                          size="small"
                          disabled={isLoading}
                        >
                          <CloseIcon />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    <label className="upload_image no_radius" htmlFor="icon-button-file">
                      <Input
                        sx={{ display: "none" }}
                        id="icon-button-file"
                        type="file"
                        inputProps={{ accept: "image/png,image/jpeg" }}
                        onChange={handleImageChange}
                        disabled={isLoading}
                      />

                      <span className="upload_image_holder">
                        <figure className="add_img">
                          <img src="/static/images/placeholder.png" alt="placeholder" />
                          <span className="upload_img_icon">
                            <img src="/static/images/edit2_icon.svg" alt="Upload" />
                          </span>
                        </figure>
                      </span>
                    </label>
                  )}
                  {errors.image && (
                    <FormHelperText error sx={{ marginTop: "4px" }}>
                      {errors.image}
                    </FormHelperText>
                  )}
                </div>

                {/* Category */}
                <div className="control_group w_50">
                  <label>
                    Category<span className="req">*</span>
                  </label>
                  <Select
                    fullWidth
                    value={category}
                    onChange={handleCategoryChange}
                    error={!!errors.category}
                    disabled={isLoading}
                  >
                    <MenuItem value="" disabled>
                      Select Category
                    </MenuItem>
                    {categories.map((cat: any) => (
                      <MenuItem key={cat._id || cat.id} value={cat._id || cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.category && (
                    <FormHelperText error sx={{ marginTop: "4px" }}>
                      {errors.category}
                    </FormHelperText>
                  )}
                </div>

                {/* Name */}
                <div className="control_group w_50">
                  <label>
                    Name<span className="req">*</span>
                  </label>
                  <TextField
                    fullWidth
                    hiddenLabel
                    placeholder="Enter Name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (errors.name) {
                        setErrors((prev) => ({ ...prev, name: undefined }));
                      }
                    }}
                    error={!!errors.name}
                    disabled={isLoading}
                  />
                  {errors.name && (
                    <FormHelperText error sx={{ marginTop: "4px" }}>
                      {errors.name}
                    </FormHelperText>
                  )}
                </div>

                {/* Ingredients */}
                <div className="control_group">
                  <label>
                    Ingredients<span className="req">*</span>
                  </label>
                  <TextField
                    fullWidth
                    hiddenLabel
                    placeholder="Enter Ingredients"
                    value={ingredients}
                    onChange={(e) => {
                      setIngredients(e.target.value);
                      if (errors.ingredients) {
                        setErrors((prev) => ({ ...prev, ingredients: undefined }));
                      }
                    }}
                    error={!!errors.ingredients}
                    disabled={isLoading}
                  />
                  {errors.ingredients && (
                    <FormHelperText error sx={{ marginTop: "4px" }}>
                      {errors.ingredients}
                    </FormHelperText>
                  )}
                </div>

                {/* Description */}
                <div className="control_group">
                  <label>
                    Description<span className="req">*</span>
                  </label>
                  <TextField
                    fullWidth
                    hiddenLabel
                    placeholder="Enter Description"
                    multiline
                    rows={4}
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      if (errors.description) {
                        setErrors((prev) => ({ ...prev, description: undefined }));
                      }
                    }}
                    error={!!errors.description}
                    disabled={isLoading}
                  />
                  {errors.description && (
                    <FormHelperText error sx={{ marginTop: "4px" }}>
                      {errors.description}
                    </FormHelperText>
                  )}
                </div>

                {/* Type */}
                <div className="control_group w_50">
                  <label>
                    Type<span className="req">*</span>
                  </label>
                  <Select
                    fullWidth
                    value={type}
                    onChange={handleTypeChange}
                    error={!!errors.type}
                    disabled={isLoading}
                  >
                    <MenuItem value="0" disabled>
                      Select Type
                    </MenuItem>
                    <MenuItem value="size">Size</MenuItem>
                    <MenuItem value="variant">Variant</MenuItem>
                  </Select>
                  {errors.type && (
                    <FormHelperText error sx={{ marginTop: "4px" }}>
                      {errors.type}
                    </FormHelperText>
                  )}
                </div>

                {/* ================= SIZE SELECTED ================= */}
                {type === "size" && (
                  <>
                    <div className="control_group w_50">
                      <label>
                        Size<span className="req">*</span>
                      </label>
                      <TextField
                        fullWidth
                        hiddenLabel
                        placeholder="Enter Size (e.g., 500)"
                        type="number"
                        value={size}
                        onChange={(e) => {
                          setSize(e.target.value);
                          if (errors.size) {
                            setErrors((prev) => ({ ...prev, size: undefined }));
                          }
                        }}
                        error={!!errors.size}
                        disabled={isLoading}
                      />
                      {errors.size && (
                        <FormHelperText error sx={{ marginTop: "4px" }}>
                          {errors.size}
                        </FormHelperText>
                      )}
                    </div>

                    <div className="control_group w_50">
                      <label>
                        Price<span className="req">*</span>
                      </label>
                      <TextField
                        fullWidth
                        hiddenLabel
                        placeholder="Enter Price (e.g., 3.5)"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        value={price}
                        onChange={(e) => {
                          setPrice(e.target.value);
                          if (errors.price) {
                            setErrors((prev) => ({ ...prev, price: undefined }));
                          }
                        }}
                        error={!!errors.price}
                        disabled={isLoading}
                      />
                      {errors.price && (
                        <FormHelperText error sx={{ marginTop: "4px" }}>
                          {errors.price}
                        </FormHelperText>
                      )}
                    </div>
                  </>
                )}

                {/* ================= VARIANT SELECTED ================= */}
                {type === "variant" && (
                  <>
                    <div className="control_group w_50">
                      <label>
                        Variant<span className="req">*</span>
                      </label>
                      <Select
                        fullWidth
                        value={variant}
                        onChange={handleVariantChange}
                        error={!!errors.variant}
                        disabled={isLoading}
                      >
                        <MenuItem value="0" disabled>
                          Choose Variant
                        </MenuItem>
                        <MenuItem value="single">Single Variant</MenuItem>
                        <MenuItem value="double">Double Variant</MenuItem>
                      </Select>
                      {errors.variant && (
                        <FormHelperText error sx={{ marginTop: "4px" }}>
                          {errors.variant}
                        </FormHelperText>
                      )}
                    </div>

                    {/* Variant = Single */}
                    {variant === "single" && (
                      <div className="control_group w_50">
                        <label>
                          Price<span className="req">*</span>
                        </label>
                        <TextField
                          fullWidth
                          hiddenLabel
                          placeholder="Enter Price (e.g., 3.5)"
                          type="number"
                          inputProps={{ step: "0.01" }}
                          value={price}
                          onChange={(e) => {
                            setPrice(e.target.value);
                            if (errors.price) {
                              setErrors((prev) => ({ ...prev, price: undefined }));
                            }
                          }}
                          error={!!errors.price}
                          disabled={isLoading}
                        />
                        {errors.price && (
                          <FormHelperText error sx={{ marginTop: "4px" }}>
                            {errors.price}
                          </FormHelperText>
                        )}
                      </div>
                    )}

                    {/* Variant = Double */}
                    {variant === "double" && (
                      <>
                        <div className="control_group w_50">
                          <label>
                            Single Price<span className="req">*</span>
                          </label>
                          <TextField
                            fullWidth
                            hiddenLabel
                            placeholder="Enter Single Price (e.g., 3.5)"
                            type="number"
                            inputProps={{ step: "0.01" }}
                            value={price}
                            onChange={(e) => {
                              setPrice(e.target.value);
                              if (errors.price) {
                                setErrors((prev) => ({ ...prev, price: undefined }));
                              }
                            }}
                            error={!!errors.price}
                            disabled={isLoading}
                          />
                          {errors.price && (
                            <FormHelperText error sx={{ marginTop: "4px" }}>
                              {errors.price}
                            </FormHelperText>
                          )}
                        </div>

                        <div className="control_group w_50">
                          <label>
                            Double Price<span className="req">*</span>
                          </label>
                          <TextField
                            fullWidth
                            hiddenLabel
                            placeholder="Enter Double Price (e.g., 6.0)"
                            type="number"
                            inputProps={{ step: "0.01" }}
                            value={doublePrice}
                            onChange={(e) => {
                              setDoublePrice(e.target.value);
                              if (errors.doublePrice) {
                                setErrors((prev) => ({ ...prev, doublePrice: undefined }));
                              }
                            }}
                            error={!!errors.doublePrice}
                            disabled={isLoading}
                          />
                          {errors.doublePrice && (
                            <FormHelperText error sx={{ marginTop: "4px" }}>
                              {errors.doublePrice}
                            </FormHelperText>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              <div className="footer_btn_flex">
                <Button
                  className="btn btn_grey"
                  onClick={() => setOpen(false)}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  className="btn btn_primary"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update Product" : "Add Product")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
}
